import { useEffect, useState } from 'react';
import { GATE_FIXTURE, type ImportFiles } from '@zrc/core';
import type { ControllerSession } from '../controller.js';
import { readFileText } from './common.js';

/** In-Zoom checks the operator needs before the evening. */
export function PrerequisitesPanel({ session }: { session: ControllerSession }) {
  const ctx = session.context;
  const checks: { label: string; ok: boolean | null; detail: string }[] = [
    { label: 'Controller running inside the Zoom client', ok: session.adapterKind === 'zoom', detail: session.adapterKind === 'zoom' ? 'yes' : 'simulator (no real rooms will move)' },
    { label: 'Operator is host or co-host', ok: ctx ? ctx.role === 'host' || ctx.role === 'cohost' : null, detail: ctx?.role ?? 'unknown' },
    { label: 'Operator is in the main session', ok: ctx ? ctx.location === 'main' : null, detail: ctx?.location ?? 'unknown' },
    { label: 'Backend reachable (persistence and control lease)', ok: session.mode === 'backend', detail: session.mode },
    { label: 'Licence capacity covers expected attendance', ok: null, detail: 'confirm against the hosting account plan (spec section 3)' },
  ];
  return (
    <div>
      <h2>Prerequisites</h2>
      <table>
        <tbody>
          {checks.map((c) => (
            <tr key={c.label}>
              <td>{c.label}</td>
              <td>{c.ok === null ? 'check manually' : c.ok ? 'pass' : 'fail'}</td>
              <td className="muted">{c.detail}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/** CSV import and validation. Admin work, done in the browser after CrtQ sign-in. */
export function ImportPanel({ session }: { session: ControllerSession }) {
  const rec = session.engine?.record ?? null;
  const [files, setFiles] = useState<Partial<ImportFiles>>({});
  if (!rec) return null;
  return (
    <div>
      <h2>Import</h2>
      {(['participants', 'rooms', 'rounds', 'assignments'] as const).map((k) => (
        <div className="row" key={k}>
          <label htmlFor={`file-${k}`}>{k}.csv</label>
          <input
            id={`file-${k}`}
            type="file"
            accept=".csv,text/csv"
            onChange={async (e) => {
              const f = e.target.files?.[0];
              if (f) setFiles((s) => ({ ...s, [k]: undefined }));
              if (f) {
                const text = await readFileText(f);
                setFiles((s) => ({ ...s, [k]: text }));
              }
            }}
          />
          <span className="muted">{files[k] ? 'loaded' : ''}</span>
        </div>
      ))}
      <div className="row">
        <button
          className="primary"
          disabled={!(files.participants && files.rooms && files.rounds && files.assignments)}
          onClick={() => void session.import(files as ImportFiles)}
        >
          Import and validate
        </button>
        <button className="secondary" onClick={() => void session.import(GATE_FIXTURE)}>
          Load gate fixture (2 rooms, 6 people, 3 rounds)
        </button>
      </div>
      {rec.importErrors.length > 0 && (
        <div className="banner banner-error">
          <strong>{rec.importErrors.length} row error(s)</strong>
          <ul>
            {rec.importErrors.map((e, i) => (
              <li key={i}>
                {e.file}.csv line {e.line}
                {e.column ? `, ${e.column}` : ''}: {e.message}
              </li>
            ))}
          </ul>
        </div>
      )}
      {rec.validation.length > 0 && (
        <div className={`banner ${rec.validation.some((f) => f.severity === 'error') ? 'banner-error' : 'banner-warn'}`}>
          <ul>
            {rec.validation.map((f, i) => (
              <li key={i}>
                [{f.severity}] {f.message}
              </li>
            ))}
          </ul>
        </div>
      )}

    </div>
  );
}

/** Room mapping and readiness. Operator work, done inside Zoom. */
export function RoomsPanel({ session }: { session: ControllerSession }) {
  const rec = session.engine?.record ?? null;
  if (!rec) return null;
  return (
    <div>
      <h2>Rooms</h2>
      <div className="row">
        <button className="secondary" disabled={!rec.scheduleVersion || rec.state === 'draft'} onClick={() => void session.mapRooms(true)}>
          Map rooms (reuse existing by name, create missing)
        </button>
      </div>
      {rec.scheduleVersion && (
        <table>
          <thead>
            <tr>
              <th>Room</th>
              <th>Capacity</th>
              <th>Zoom room</th>
            </tr>
          </thead>
          <tbody>
            {rec.scheduleVersion.schedule.rooms.map((r) => (
              <tr key={r.roomId}>
                <td>{r.roomName}</td>
                <td>{r.capacity}</td>
                <td>{rec.roomMap[r.roomId] ?? 'not mapped'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <h2>Readiness</h2>
      <Readiness session={session} />
    </div>
  );
}

export function SetupView({ session }: { session: ControllerSession }) {
  const rec = session.engine?.record ?? null;
  const [title, setTitle] = useState('Residency careers evening');
  const [events, setEvents] = useState<{ eventId: string; title: string; state: string }[]>([]);

  useEffect(() => {
    void session.listEvents().then(setEvents).catch(() => setEvents([]));
  }, [session, rec?.eventId]);

  return (
    <div>
      <PrerequisitesPanel session={session} />
      <h2>Event</h2>
      {!rec && (
        <div>
          <div className="row">
            <label htmlFor="title">Title</label>
            <input id="title" type="text" value={title} onChange={(e) => setTitle(e.target.value)} />
            <button className="primary" onClick={() => void session.createEvent(title, 'Europe/London')}>
              Create event
            </button>
          </div>
          {events.length > 0 && (
            <div>
              <label>Or resume</label>
              <ul>
                {events.map((e) => (
                  <li key={e.eventId}>
                    <button className="secondary" onClick={() => void session.loadEvent(e.eventId)}>
                      {e.title} ({e.state})
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
      {rec && (
        <p>
          {rec.title} · timezone {rec.timezone} · state {rec.state} · schedule {rec.scheduleVersion ? `v${rec.scheduleVersion.version} (${rec.scheduleVersion.hash})` : 'none'}
        </p>
      )}

      {rec && (
        <>
          <ImportPanel session={session} />
          <RoomsPanel session={session} />
        </>
      )}
    </div>
  );
}

export function Readiness({ session }: { session: ControllerSession }) {
  const eng = session.engine;
  if (!eng || !eng.record.scheduleVersion) return <p className="muted">Import a schedule first.</p>;
  const r = eng.readiness();
  return (
    <div>
      {r.ready ? <div className="banner banner-info">Ready to launch.</div> : (
        <ul>
          {r.problems.map((p) => (
            <li key={p}>{p}</li>
          ))}
        </ul>
      )}
      <button className="primary" disabled={!r.ready || eng.record.state === 'ready'} onClick={() => void session.markReady()}>
        Mark ready
      </button>
    </div>
  );
}
